import React from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ISSUE_TABLE_ROW_INNER_HEIGHT } from '../../../common/constants.tsx';
import type { Count, Trend } from '../../../common/types.tsx';
import { TrendIcon } from '../../../common/ui/trend-icon/index.tsx';
import { getTrend } from '../../../common/utils/trend.tsx';
import messages from './messages.tsx';

type Props = {
	issueCount: Count;
	medianIssuesPerDeployment: Count;
	onClick: () => void;
};

const getIconTooltipTranslatedMessage = (trend: Trend, formatMessage: FormatMessage): string => {
	switch (trend) {
		case 'under':
			return formatMessage(messages.underTwelveWeekMedian);
		case 'over':
			return formatMessage(messages.overTwelveWeekMedian);
		case 'equal':
			return '';
		default:
			// prettier-ignore
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(trend as never);
			throw new Error('Unknown trend is given to getIconTooltipTranslatedMessage');
	}
};

export const JiraIssuesPerDeploymentCellContent = ({
	medianIssuesPerDeployment,
	issueCount,
	onClick,
}: Props) => {
	const { formatMessage } = useIntl();
	const trend = getTrend(issueCount, medianIssuesPerDeployment);

	return (
		<Wrapper onClick={onClick}>
			<Tooltip content={getIconTooltipTranslatedMessage(trend, formatMessage)} position="right">
				<TrendIconWrapper>
					<TrendIcon trend={trend} colorScheme="UP_IS_BAD" />
				</TrendIconWrapper>
			</Tooltip>
			<IssueCount>
				{fg('jira-issue-terminology-refresh-m3')
					? formatMessage(messages.numIssuesIssueTermRefresh, { numIssues: issueCount })
					: formatMessage(messages.numIssues, { numIssues: issueCount })}
			</IssueCount>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${ISSUE_TABLE_ROW_INNER_HEIGHT}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TrendIconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 2}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueCount = styled.button({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	border: 'none',
	padding: 0,
	background: 'transparent',
	marginLeft: token('space.050'),
	textDecoration: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.link', colors.B400),
	font: token('font.body'),
	cursor: 'pointer',
	'&:hover': {
		textDecoration: 'none',
	},
});
